import { CircleAlert, Pencil, TriangleAlert } from "lucide-react";
import { LIFECYCLE_LABEL } from "../lifecycleStates";
import type { ImportPreviewRow, LifecycleState } from "../types";
import { LifecycleStateSelect } from "./LifecycleStateSelect";

type Props = {
  rows: ImportPreviewRow[];
  editedStates: Map<number, LifecycleState>;
  duplicatesInFile: Set<string>;
  duplicatesInAccount: Set<string>;
  onLifecycleChange: (rowIndex: number, next: LifecycleState) => void;
};

export function ImportPreviewTable({
  rows,
  editedStates,
  duplicatesInFile,
  duplicatesInAccount,
  onLifecycleChange,
}: Props) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-app-ink-3 text-sm">
        No hay filas listas para importar en este archivo.
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-2xl border border-app-border bg-app-surface"
      data-testid="import-preview-table"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-app-border border-b bg-app-surface-2 text-app-ink-3 text-xs uppercase tracking-wider">
            <Th>Tag</Th>
            <Th>Nombre</Th>
            <Th>Sexo</Th>
            <Th className="w-[260px]">Estado</Th>
            <Th>Lote</Th>
            <Th className="w-[40px]">{""}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const current = editedStates.get(row.rowIndex) ?? row.suggestedLifecycleState;
            const isOverridden = current !== row.suggestedLifecycleState;
            const isDupInFile = duplicatesInFile.has(row.tag.toLowerCase());
            const isDupInAccount = duplicatesInAccount.has(row.tag.toLowerCase());
            return (
              <tr
                key={row.rowIndex}
                className="border-app-border border-b last:border-b-0"
                data-testid={`import-row-${row.rowIndex}`}
              >
                <Td className="font-mono tabular-nums">{row.tag}</Td>
                <Td>{row.name ?? <span className="text-app-ink-3">—</span>}</Td>
                <Td>{row.sex === "female" ? "♀" : "♂"}</Td>
                <Td>
                  <div className="flex items-center gap-1.5">
                    <LifecycleStateSelect
                      value={current}
                      onChange={(next) => onLifecycleChange(row.rowIndex, next)}
                      ariaLabel={`Estado para ${row.tag}`}
                    />
                    {isOverridden ? (
                      <span
                        title={`Sugerido por el sistema: ${LIFECYCLE_LABEL[row.suggestedLifecycleState]}`}
                        className="text-app-ink-3"
                        data-testid={`row-${row.rowIndex}-overridden`}
                      >
                        <Pencil size={14} strokeWidth={2} />
                      </span>
                    ) : null}
                  </div>
                </Td>
                <Td>{row.lotName}</Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {isDupInAccount ? (
                      <span
                        title="Este tag ya existe en tu cuenta"
                        className="text-app-amber"
                        data-testid={`row-${row.rowIndex}-dup-account`}
                      >
                        <CircleAlert size={16} strokeWidth={2} />
                      </span>
                    ) : isDupInFile ? (
                      <span
                        title="Este tag aparece duplicado dentro del archivo"
                        className="text-app-amber"
                        data-testid={`row-${row.rowIndex}-dup-file`}
                      >
                        <CircleAlert size={16} strokeWidth={2} />
                      </span>
                    ) : null}
                    {row.warnings.length > 0 ? (
                      <span
                        title={row.warnings.join(" · ")}
                        className="text-app-amber"
                        data-testid={`row-${row.rowIndex}-warnings`}
                      >
                        <TriangleAlert size={16} strokeWidth={2} />
                      </span>
                    ) : null}
                  </div>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium ${className}`}>{children}</th>;
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-middle text-app-ink-1 ${className}`}>{children}</td>;
}
